import MuiTimeline from "@mui/lab/Timeline";
import MuiTimelineConnector from "@mui/lab/TimelineConnector";
import MuiTimelineContent from "@mui/lab/TimelineContent";
import MuiTimelineDot from "@mui/lab/TimelineDot";
import MuiTimelineItem from "@mui/lab/TimelineItem";
import MuiTimelineSeparator from "@mui/lab/TimelineSeparator";
import { styled } from "@mui/material/styles";

import type { HbmlElementNode, HbmlNode } from "@holibob-packages/hbml";
import { sanitizeHbml } from "@holibob-packages/hbml";

import { HbmlRendererNodes, ReadMoreContainer } from "../../components";
import { HbmlElements } from "../../components/HbmlComponents/HbmlElements";
import { Typography } from "../../components/Typography";
import { ProductItineraryItemFragment } from "../../gql-request";
import { useLanguage, useNextTranslation } from "../../hooks";
import { formatDurationFromString } from "../../utils/formatters/formatDuration";
import { LabelList, LabelListItem } from "./LabelList";
import { ProductContentHbmlRenderer } from "./ProductContentHbmlRenderer";
import { ProductExpandButton } from "./ProductExpandButton";

export type ProductItineraryProps = {
    items: readonly ProductItineraryItemFragment[];
    defaultExpanded?: boolean;
};
export const ProductItinerary = ({ items, defaultExpanded = false }: ProductItineraryProps) => {
    const uniqueDays = new Set(items.map((item) => item.extraInformation.day));
    const withPassByWithoutStopping = items.some((item) => item.extraInformation.passByWithoutStopping);

    const [t] = useNextTranslation("product");

    return (
        <Timeline>
            <ReadMoreContainer
                expandLabel={t("itinerary.seeMoreButton.label")}
                threshold={300}
                ExpandButtonComponent={ProductExpandButton}
                defaultExpanded={defaultExpanded}
            >
                {items.map((item, idx) => {
                    return (
                        <ProductItineraryItem
                            key={item.id}
                            item={item}
                            showDay={uniqueDays.size > 1}
                            showConnector={idx < items.length - 1}
                            withPassByWithoutStopping={withPassByWithoutStopping}
                        />
                    );
                })}
            </ReadMoreContainer>
        </Timeline>
    );
};

const Timeline = styled(MuiTimeline)({
    padding: 0,
    margin: 0,
});

type ProductItineraryItemProps = {
    item: ProductItineraryItemFragment;
    showDay: boolean;
    showConnector: boolean;
    withPassByWithoutStopping: boolean;
};

const ItineraryListItemText = styled(Typography)({
    verticalAlign: "middle",
});

const COMPONENT_OVERRIDES = {
    [HbmlElements.PARAGRAPH]: function ParagraphNode({ content }: { content: HbmlElementNode }) {
        return (
            <Typography variant="body" size="large" color="subtle">
                <HbmlRendererNodes content={content.children as HbmlNode[]} />
            </Typography>
        );
    },
};

type ProductItineraryItemTitleProps = {
    name: string;
    withPassByWithoutStopping: boolean;
};

const ProductItineraryItemTitle = (props: ProductItineraryItemTitleProps) => {
    const { name, withPassByWithoutStopping } = props;
    const [t] = useNextTranslation("product");
    const effectiveName = withPassByWithoutStopping ? `${name} ` : name;

    return (
        <div>
            <ProductItineraryItemTitleText variant="title" size="medium" fontWeight="bold" data-inline={true}>
                {effectiveName}
            </ProductItineraryItemTitleText>
            {withPassByWithoutStopping && (
                <ProductItineraryItemTitleText component="label" variant="body" size="small" color="subtle">
                    {`(${t("itinerary.meta.passByWithoutStopping")})`}
                </ProductItineraryItemTitleText>
            )}
        </div>
    );
};
const ProductItineraryItem = (props: ProductItineraryItemProps) => {
    const { item, showDay, showConnector, withPassByWithoutStopping } = props;
    const [t] = useNextTranslation("product");
    const { extraInformation, name, description, duration } = item;
    if (!name && !description) return null;
    const { day } = extraInformation;

    return (
        <TimelineItem>
            <TimelineSeparator data-ref="product-itinerary-item-timeline-separator">
                <TimelineDot variant="outlined" color="primary" />
                {showConnector && <TimelineConnector color="primary" />}
            </TimelineSeparator>
            <TimelineContent data-ref="product-itinerary-item-timeline-content">
                {name && (
                    <ProductItineraryItemTitle name={name} withPassByWithoutStopping={withPassByWithoutStopping} />
                )}
                {showDay && day && (
                    <Typography variant="body" size="small" color="subtle">
                        {t("itinerary.meta.day")} {day}
                    </Typography>
                )}
                {description && (
                    <ProductContentHbmlRenderer
                        content={sanitizeHbml(description.enhanced)}
                        componentOverrides={COMPONENT_OVERRIDES}
                    />
                )}
                <ProductItineraryItemMeta extraInformation={extraInformation} duration={duration} />
            </TimelineContent>
        </TimelineItem>
    );
};

type ProductItineraryItemMetaProps = Pick<ProductItineraryItemFragment, "extraInformation" | "duration">;
const ProductItineraryItemMeta = ({ extraInformation, duration }: ProductItineraryItemMetaProps) => {
    const { isAdmissionIncluded, passByWithoutStopping } = extraInformation;
    const language = useLanguage();
    const [t] = useNextTranslation("product");

    if (!isAdmissionIncluded && !duration && typeof passByWithoutStopping !== "boolean") return null;

    return (
        <LabelList>
            {duration && (
                <LabelListItem>
                    <ItineraryListItemText variant="body" size="small" color="subtle" component="span">
                        {formatDurationFromString(language, duration, duration)}
                    </ItineraryListItemText>
                </LabelListItem>
            )}
            {isAdmissionIncluded && (
                <LabelListItem>
                    <ItineraryListItemText variant="body" size="small" color="subtle" component="span">
                        {t("itinerary.meta.admissionIncluded")}
                    </ItineraryListItemText>
                </LabelListItem>
            )}
        </LabelList>
    );
};

const TimelineDot = styled(MuiTimelineDot)({
    margin: 0,
});

const TimelineItem = styled(MuiTimelineItem)({
    minHeight: 30,
    "&:before": {
        padding: 0,
        flex: 0,
    },
});
const TimelineSeparator = styled(MuiTimelineSeparator)({
    transform: "translate(0,10px)",
});
const TimelineConnector = styled(MuiTimelineConnector)(({ theme }) => ({
    borderStyle: "dashed",
    borderWidth: 1,
    borderColor: theme.palette.primary.main,
    background: "unset",
    width: 0,
}));
const TimelineContent = styled(MuiTimelineContent)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
}));
const ProductItineraryItemTitleText = styled(Typography)(({ theme }) => ({
    "&&&": { lineHeight: "24px" },
    display: "inline-block",
    verticalAlign: "middle",
    marginRight: theme.spacing(0.5),
    ["&[data-inline='true']"]: {
        display: "inline",
    },
}));
